import { useCallback, useEffect, useState } from "react";
import { Lock, Check, X, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { LOCK_REQUESTS_CHANGED } from "@/hooks/usePendingLockRequests";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: "pending", label: "En attente" },
  { id: "approved", label: "Acceptées" },
  { id: "rejected", label: "Refusées" },
  { id: "all", label: "Toutes" },
];

const STATUS_STYLE = {
  pending: { label: "En attente", bg: "#D4A37320", fg: "#8B5E3C" },
  approved: { label: "Acceptée", bg: "#2D6A4F18", fg: "#1f5c39" },
  rejected: { label: "Refusée", bg: "#E07A5F18", fg: "#B4532F" },
  cancelled: { label: "Annulée", bg: "#0000000A", fg: "#7A7A7A" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

/**
 * Onglet admin « Demandes de verrou » : les utilisateurs demandent le
 * verrouillage d'une parcelle depuis la carte, l'admin accepte ou refuse.
 * Le rattachement manuel (onglet Utilisateurs) reste disponible à part.
 */
export default function LockRequestsPanel() {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/lock-requests?status=${filter}`);
      if (res.ok) setItems(await res.json());
      else toast.error("Chargement impossible");
      window.dispatchEvent(new Event(LOCK_REQUESTS_CHANGED));
    } catch { toast.error("Erreur de connexion"); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusy(r.request_id);
    try {
      const res = await authFetch(`${API_BASE}/admin/lock-requests/${r.request_id}/approve`, { method: "POST" });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Parcelle verrouillée au nom de ${r.user_name || r.user_email}`
          + (d.auto_rejected ? ` · ${d.auto_rejected} autre(s) demande(s) close(s)` : ""));
        load();
      } else {
        toast.error(typeof d.detail === "string" ? d.detail : "Validation impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
    setBusy(null);
  };

  const reject = async (r) => {
    setBusy(r.request_id);
    try {
      const res = await authFetch(`${API_BASE}/admin/lock-requests/${r.request_id}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: reason.trim() || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { toast.success("Demande refusée"); setRejecting(null); setReason(""); load(); }
      else toast.error(typeof d.detail === "string" ? d.detail : "Refus impossible");
    } catch { toast.error("Erreur de connexion"); }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="lock-requests-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Lock className="w-4 h-4" style={{ color: "#2D6A4F" }} />
        <h2 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>Demandes de verrouillage</h2>
        <div className="ml-auto flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className="text-[11px] font-semibold rounded-full px-3 py-1 border"
              style={filter === f.id ? { background: "#2D6A4F", color: "#fff", borderColor: "#2D6A4F" } : { background: "#fff", color: "#4A4A4A", borderColor: "#0000001A" }}
              data-testid={`lock-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-[11px]" style={{ color: "#7A7A7A" }}>
        Accepter verrouille la parcelle au nom du demandeur (sans paiement ni plafond), comme le rattachement manuel de l'onglet Utilisateurs, qui reste disponible.
      </p>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <p className="text-xs italic py-6 text-center" style={{ color: "#7A7A7A" }}>
          {filter === "pending" ? "Aucune demande en attente." : "Aucune demande."}
        </p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.cancelled;
            const takenByOther = r.status === "pending" && r.parcel_locked_by_email;
            return (
              <div key={r.request_id} className="rounded-xl border border-black/10 bg-white p-3 space-y-2" data-testid={`lock-request-${r.request_id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold truncate" style={{ color: "#1A1A1A" }}>
                      {r.parcel?.name || r.parcel_id}
                    </div>
                    <div className="text-[11px]" style={{ color: "#7A7A7A" }}>
                      {r.parcel?.commune || "—"} · {r.parcel?.culture_type || "—"} · {r.parcel?.surface_ha ?? "?"} ha ·{" "}
                      <a href={`/map?parcel=${encodeURIComponent(r.parcel_id)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 underline">
                        {r.parcel_id} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <span className="text-[10.5px] font-semibold rounded-full px-2 py-0.5 shrink-0" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                </div>
                <div className="text-[12px]" style={{ color: "#4A4A4A" }}>
                  <span className="font-semibold">{r.user_name || r.user_email}</span>
                  {r.user_name && <span className="text-[11px]" style={{ color: "#7A7A7A" }}> · {r.user_email}</span>}
                  <span className="text-[11px]" style={{ color: "#7A7A7A" }}> · {r.user_locked_count} parcelle(s) déjà verrouillée(s) · demandé le {formatDate(r.created_at)}</span>
                </div>
                {r.cadastre_refs?.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap text-[11px]" style={{ color: "#7A7A7A" }} data-testid={`lock-request-cadastre-${r.request_id}`}>
                    Cadastre :
                    {r.cadastre_refs.map((c) => (
                      <span key={c.idu} className="font-mono rounded px-1.5 py-0.5" style={{ background: "#FBEDE8", color: "#B4532F" }}
                        title={`${c.commune} · ${c.surface_ha} ha · ${c.share_of_parcel_pct ?? "?"} % de la parcelle`}>
                        {String(c.section).replace(/^0/, "")} {parseInt(c.numero, 10)}
                      </span>
                    ))}
                  </div>
                )}
                {r.message && (
                  <p className="text-[12px] italic rounded-lg px-2.5 py-1.5" style={{ background: "#F9F8F6", color: "#4A4A4A" }}>« {r.message} »</p>
                )}
                {takenByOther && (
                  <p className="text-[11px] flex items-center gap-1" style={{ color: "#B4532F" }}>
                    <AlertTriangle className="w-3.5 h-3.5" /> Parcelle déjà verrouillée par {r.parcel_locked_by_email} : détachez-la d'abord pour accepter.
                  </p>
                )}
                {r.status !== "pending" && r.decided_at && (
                  <p className="text-[11px]" style={{ color: "#7A7A7A" }}>
                    Traitée le {formatDate(r.decided_at)}{r.decided_by ? ` par ${r.decided_by}` : ""}{r.reason ? ` · motif : ${r.reason}` : ""}
                  </p>
                )}
                {r.status === "pending" && (
                  rejecting === r.request_id ? (
                    <div className="flex gap-1.5 flex-wrap">
                      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300}
                        placeholder="Motif (facultatif, envoyé au demandeur)"
                        className="flex-1 min-w-[180px] h-8 text-xs border border-black/15 rounded-md px-2" data-testid="lock-reject-reason" />
                      <button onClick={() => reject(r)} disabled={busy === r.request_id}
                        className="h-8 px-3 rounded-md text-[11px] font-semibold text-white" style={{ background: "#E07A5F" }} data-testid="lock-reject-confirm">
                        {busy === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirmer le refus"}
                      </button>
                      <button onClick={() => { setRejecting(null); setReason(""); }} className="h-8 px-3 rounded-md text-[11px] border border-black/15">Annuler</button>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <button onClick={() => approve(r)} disabled={busy === r.request_id || !!takenByOther}
                        className="h-8 px-3 rounded-md text-[11px] font-semibold text-white inline-flex items-center gap-1 disabled:opacity-50" style={{ background: "#2D6A4F" }} data-testid="lock-approve">
                        {busy === r.request_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Accepter et verrouiller
                      </button>
                      <button onClick={() => setRejecting(r.request_id)} disabled={busy === r.request_id}
                        className="h-8 px-3 rounded-md text-[11px] font-semibold border inline-flex items-center gap-1" style={{ borderColor: "#E07A5F66", color: "#B4532F" }} data-testid="lock-reject">
                        <X className="w-3.5 h-3.5" /> Refuser
                      </button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
